import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { Box, Radio, Typography, useTheme } from '@popmenu/common-ui';
import { useFormContext } from 'react-form';
import { FormattedMessage } from 'react-intl';
import Grid from '~/shared/Grid';
import Loading from '~/shared/Loading';
import type { RootSiteTemplate } from '~/libs/gql/types';
import cn from 'classnames';
import useStyles from './styles';
import { useSiteInformationStep } from './hooks/useSiteInformationStep';
import WebsiteFields from './WebsiteFields';
import Template from './Template';
import type { SelfSignUpForm } from '../types';

const SelfSignUpSiteInformation = () => {
  const { setFieldValue, values } = useFormContext<SelfSignUpForm>();
  const { data, handleLoadMore, getScrollParent } = useSiteInformationStep();
  const { spacing } = useTheme();
  const classes = useStyles();

  const selectedRootSiteTemplateId = values.selectedRootSiteTemplate?.id;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" width="100%">
      <Grid container xs={12} md={7}>
        <Grid item xs={12}>
          <Typography variant="h5">
            <FormattedMessage
              defaultMessage="Help us to get your website started"
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <WebsiteFields />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">
            <FormattedMessage
              defaultMessage="Choose a design template"
            />
          </Typography>
          <Typography variant="body1" className={classes.description}>
            <FormattedMessage
              defaultMessage="Don't worry, you can always switch to another template later while customizing your site’s branding and content."
            />
          </Typography>
        </Grid>
        <br />
        <br />
        <Grid item xs={12} data-tour-id="sign-up-site-information-step-choose-design-template">
          {data?.rootSiteTemplates ? (
            <InfiniteScroll
              loadMore={handleLoadMore}
              hasMore={data.rootSiteTemplates.hasMore}
              loader={<Loading key="loading-templates" />}
              getScrollParent={getScrollParent}
              useWindow={false}
            >
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                gridGap={spacing(2)}
              >
                {data.rootSiteTemplates.records.map((template) => {
                  const isChecked = template.id === Number(selectedRootSiteTemplateId);

                  return (
                    <Radio
                      key={template.id}
                      label={template.name}
                      value={template.id}
                      data-testid={`selectedRootSiteTemplateId_radio_${template.id}`}
                      onChange={() => setFieldValue('selectedRootSiteTemplate', template as RootSiteTemplate)}
                      checked={isChecked}
                      className={cn(classes.radioButton, {
                        [classes.radioButtonChecked]: isChecked,
                      })}
                      LabelProps={{
                        className: classes.formLabel,
                        label: <Template template={template as RootSiteTemplate} />,
                      }}
                    />
                  );
                })}
              </Box>
            </InfiniteScroll>
          ) : <Loading size="lg" />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SelfSignUpSiteInformation;
